import type { AudioPreferences } from '~/stores/usePlayerStore'

type BgmContext =
  | 'menu'
  | 'route'
  | 'forest'
  | 'cave'
  | 'ocean'
  | 'volcano'
  | 'snow'
  | 'league'
  | 'boss'
type SfxName =
  | 'uiToggle'
  | 'pokeballThrow'
  | 'pokeballLock'
  | 'encounter'
  | 'shinyEncounter'
  | 'bossStart'
  | 'enemyDefeated'
  | 'bossDefeated'
  | 'bossFailed'
  | 'levelUp'
  | 'badge'
  | 'summon'
type AudioPack = AudioPreferences['audioPack']

const state = reactive({
  initialized: false,
  audioCtx: null as AudioContext | null,
  unlocked: false,
  currentBgm: 'menu' as BgmContext,
  bgmOscillators: [] as OscillatorNode[],
  bgmGains: [] as GainNode[],
  bgmStepTimer: null as ReturnType<typeof setTimeout> | null,
  bgmContextTimer: null as ReturnType<typeof setTimeout> | null,
  bgmStep: 0,
  generationContext: 1,
  lastSfxAt: {} as Record<string, number>,
  cryCache: new Map<string, string | null>(),
  externalManifestLoaded: false,
  externalManifestFailed: false,
  externalLastTrackByContext: {} as Partial<Record<BgmContext, string>>,
  externalCatalog: [] as string[],
  externalTracksByContext: {
    menu: [],
    route: [],
    forest: [],
    cave: [],
    ocean: [],
    volcano: [],
    snow: [],
    league: [],
    boss: [],
  } as Record<BgmContext, string[]>,
  externalAudio: null as HTMLAudioElement | null,
  externalFadingOutAudio: null as HTMLAudioElement | null,
  externalFadeTimer: null as number | null,
  externalTransitionToken: 0,
  externalProgressTimer: null as ReturnType<typeof setInterval> | null,
  shinyJingleAudio: null as HTMLAudioElement | null,
  /** URL canonique (catalogue) du morceau streaming en cours — utile quand `externalTrackUrl` est vide (jukebox). */
  externalPlayingCanonicalSrc: null as string | null,
})

const STREAMING_PROGRESS_KEY = 'poke-idle-streaming-progress-v1'
const CRY_CACHE_MAX = 200
/** Pool réutilisable pour cris / samples courts — évite des milliers de `new Audio()` en session longue. */
const ONE_SHOT_POOL_SIZE = 3
const oneShotPool: HTMLAudioElement[] = []
let oneShotPoolIdx = 0

function releaseOneShotElement(audio: HTMLAudioElement) {
  try {
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
  } catch {
    // ignore
  }
}

function getOneShotAudio(): HTMLAudioElement {
  if (oneShotPool.length < ONE_SHOT_POOL_SIZE) {
    const el = new Audio()
    el.preload = 'auto'
    oneShotPool.push(el)
    return el
  }
  const el = oneShotPool[oneShotPoolIdx]!
  oneShotPoolIdx = (oneShotPoolIdx + 1) % ONE_SHOT_POOL_SIZE
  releaseOneShotElement(el)
  return el
}

function playOneShotUrl(url: string, volume: number): boolean {
  if (!url || !state.unlocked) return false
  try {
    const audio = getOneShotAudio()
    audio.volume = Math.max(0, Math.min(1, volume))
    audio.src = url
    void audio.play().catch(() => {
      // ignore
    })
    audio.addEventListener(
      'ended',
      () => {
        releaseOneShotElement(audio)
      },
      { once: true },
    )
    return true
  } catch {
    return false
  }
}

function setCryCacheEntry(slug: string, url: string | null) {
  if (state.cryCache.has(slug)) state.cryCache.delete(slug)
  state.cryCache.set(slug, url)
  while (state.cryCache.size > CRY_CACHE_MAX) {
    const oldest = state.cryCache.keys().next().value
    if (oldest === undefined) break
    state.cryCache.delete(oldest)
  }
}

const prefs = reactive<AudioPreferences>({
  enabled: false,
  musicEnabled: true,
  sfxEnabled: true,
  sfxUiEnabled: true,
  sfxInvocationEnabled: true,
  sfxEncounterEnabled: true,
  sfxShinyEncounterEnabled: true,
  sfxBossEventsEnabled: true,
  sfxCombatEnabled: true,
  sfxProgressEnabled: true,
  criesEnabled: true,
  wildKillSfxEnabled: false,
  musicMode: 'generated',
  externalTrackUrl: '',
  resumeStreamingFromLastPosition: true,
  shinyEasterEggEnabled: false,
  audioPack: 'classic8',
  autoPackByGeneration: false,
  streamingCrossfadeMs: 500,
  masterVolume: 70,
  musicVolume: 45,
  sfxVolume: 70,
})

const AUDIO_PACKS: Record<AudioPack, {
  nameFr: string
  nameEn: string
  bpmScale: number
  leadWave: OscillatorType
  bassWave: OscillatorType
  leadGainMul: number
  bassGainMul: number
}> = {
  classic8: {
    nameFr: 'Classic 8-bit',
    nameEn: 'Classic 8-bit',
    bpmScale: 1.03,
    leadWave: 'square',
    bassWave: 'square',
    leadGainMul: 0.8,
    bassGainMul: 0.65,
  },
  gba: {
    nameFr: 'GBA-like',
    nameEn: 'GBA-like',
    bpmScale: 1,
    leadWave: 'triangle',
    bassWave: 'square',
    leadGainMul: 1,
    bassGainMul: 0.8,
  },
  ds: {
    nameFr: 'DS-like',
    nameEn: 'DS-like',
    bpmScale: 0.98,
    leadWave: 'triangle',
    bassWave: 'triangle',
    leadGainMul: 1.05,
    bassGainMul: 0.95,
  },
  ostInspired: {
    nameFr: 'OST-like (généré)',
    nameEn: 'OST-like (generated)',
    bpmScale: 1.02,
    leadWave: 'triangle',
    bassWave: 'triangle',
    leadGainMul: 1.1,
    bassGainMul: 1.0,
  },
  modernRetro: {
    nameFr: 'Retro modern',
    nameEn: 'Modern retro',
    bpmScale: 0.96,
    leadWave: 'sawtooth',
    bassWave: 'triangle',
    leadGainMul: 1.15,
    bassGainMul: 0.9,
  },
}

function getGenerationAutoPack(gen: number): AudioPack {
  if (gen <= 2) return 'classic8'
  if (gen <= 4) return 'gba'
  if (gen <= 5) return 'ds'
  if (gen <= 7) return 'ostInspired'
  return 'modernRetro'
}

function getActivePack(): AudioPack {
  return prefs.autoPackByGeneration
    ? getGenerationAutoPack(state.generationContext)
    : prefs.audioPack
}

const BGM_PATTERNS: Record<BgmContext, { bpm: number; notes: number[] }> = {
  menu: { bpm: 96, notes: [261.63, 329.63, 392.0, 329.63, 293.66, 349.23, 392.0, 349.23] },
  route: { bpm: 124, notes: [293.66, 329.63, 392.0, 440.0, 392.0, 349.23, 329.63, 293.66] },
  forest: { bpm: 114, notes: [220.0, 261.63, 293.66, 349.23, 329.63, 293.66, 261.63, 246.94] },
  cave: { bpm: 108, notes: [164.81, 196.0, 220.0, 246.94, 220.0, 196.0, 174.61, 164.81] },
  ocean: { bpm: 104, notes: [261.63, 293.66, 329.63, 392.0, 349.23, 329.63, 293.66, 261.63] },
  volcano: { bpm: 140, notes: [196.0, 233.08, 293.66, 311.13, 293.66, 246.94, 220.0, 196.0] },
  snow: { bpm: 100, notes: [329.63, 392.0, 440.0, 493.88, 440.0, 392.0, 349.23, 329.63] },
  league: { bpm: 146, notes: [246.94, 293.66, 369.99, 440.0, 392.0, 329.63, 293.66, 246.94] },
  boss: { bpm: 150, notes: [196.0, 246.94, 293.66, 329.63, 311.13, 293.66, 246.94, 220.0] },
}

const OST_INSPIRED_PATTERNS: Record<BgmContext, { bpm: number; notes: number[] }> = {
  menu: { bpm: 92, notes: [261.63, 293.66, 329.63, 392.0, 329.63, 293.66, 261.63, 220.0, 246.94, 293.66] },
  route: { bpm: 126, notes: [293.66, 329.63, 392.0, 493.88, 440.0, 392.0, 349.23, 293.66, 329.63, 392.0] },
  forest: { bpm: 112, notes: [220.0, 246.94, 293.66, 329.63, 349.23, 329.63, 293.66, 261.63, 246.94, 220.0] },
  cave: { bpm: 104, notes: [164.81, 196.0, 220.0, 246.94, 261.63, 246.94, 220.0, 196.0, 174.61, 164.81] },
  ocean: { bpm: 102, notes: [261.63, 293.66, 329.63, 369.99, 392.0, 369.99, 329.63, 293.66, 261.63, 293.66] },
  volcano: { bpm: 138, notes: [196.0, 246.94, 293.66, 311.13, 329.63, 311.13, 293.66, 246.94, 220.0, 196.0] },
  snow: { bpm: 98, notes: [329.63, 392.0, 440.0, 523.25, 493.88, 440.0, 392.0, 349.23, 329.63, 293.66] },
  league: { bpm: 148, notes: [246.94, 293.66, 329.63, 392.0, 493.88, 440.0, 392.0, 369.99, 329.63, 293.66] },
  boss: { bpm: 152, notes: [196.0, 246.94, 293.66, 349.23, 392.0, 349.23, 311.13, 293.66, 246.94, 220.0] },
}

function getActivePattern(context: BgmContext): { bpm: number; notes: number[] } {
  return getActivePack() === 'ostInspired'
    ? OST_INSPIRED_PATTERNS[context]
    : BGM_PATTERNS[context]
}

function stopExternalAudio() {
  state.externalTransitionToken++
  if (state.externalFadeTimer !== null && typeof window !== 'undefined') {
    window.clearInterval(state.externalFadeTimer)
    state.externalFadeTimer = null
  }
  if (state.externalProgressTimer) {
    clearInterval(state.externalProgressTimer)
    state.externalProgressTimer = null
  }
  if (state.externalFadingOutAudio) {
    try {
      state.externalFadingOutAudio.pause()
      state.externalFadingOutAudio.src = ''
    } catch {
      // ignore
    }
    state.externalFadingOutAudio = null
  }
  if (!state.externalAudio) return
  try {
    state.externalAudio.pause()
    state.externalAudio.src = ''
  } catch {
    // ignore
  }
  state.externalAudio = null
  state.externalPlayingCanonicalSrc = null
}

function loadStreamingProgress(): { url: string; timeSec: number } | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STREAMING_PROGRESS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { url?: unknown; timeSec?: unknown }
    if (typeof parsed.url !== 'string') return null
    const t = Number(parsed.timeSec)
    if (!Number.isFinite(t) || t < 0) return null
    return { url: parsed.url, timeSec: t }
  } catch {
    return null
  }
}

function persistStreamingProgress(url: string, timeSec: number) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STREAMING_PROGRESS_KEY, JSON.stringify({
      url,
      timeSec: Math.max(0, Math.floor(timeSec)),
      updatedAt: Date.now(),
    }))
  } catch {
    // ignore
  }
}

function attachStreamingProgressTracking(audio: HTMLAudioElement, src: string) {
  if (state.externalProgressTimer) {
    clearInterval(state.externalProgressTimer)
    state.externalProgressTimer = null
  }
  state.externalProgressTimer = setInterval(() => {
    if (!state.externalAudio || state.externalAudio !== audio) return
    if (!Number.isFinite(audio.currentTime) || audio.currentTime <= 0) return
    persistStreamingProgress(src, audio.currentTime)
  }, 5000)
}

function stopShinyJingle() {
  if (!state.shinyJingleAudio) return
  try {
    state.shinyJingleAudio.pause()
    state.shinyJingleAudio.src = ''
  } catch {
    // ignore
  }
  state.shinyJingleAudio = null
}

function playEffectSample(path: string, volumeMul = 1): boolean {
  const url = toAbsoluteAudioUrl(path)
  if (!url) return false
  return playOneShotUrl(url, getSfxLinear() * volumeMul)
}

function playShinyJingleFallbackTones() {
  const vol = getSfxLinear()
  if (vol <= 0) return
  playTone(880, 100, vol * 0.18)
  setTimeout(() => playTone(1174.66, 140, vol * 0.2), 90)
  setTimeout(() => playTone(1396.91, 180, vol * 0.17), 200)
}

function playShinyJingle() {
  const primary = prefs.shinyEasterEggEnabled
    ? '/audio/effect/shiny.ogg'
    : '/audio/effect/shiny-pokemon.ogg'
  const fallback = prefs.shinyEasterEggEnabled
    ? '/audio/effect/shiny-pokemon.ogg'
    : '/audio/effect/shiny.ogg'
  stopShinyJingle()

  const tryUrl = (path: string): Promise<boolean> => {
    const url = toAbsoluteAudioUrl(path)
    if (!url) return Promise.resolve(false)
    return new Promise((resolve) => {
      try {
        const jingle = new Audio(url)
        jingle.preload = 'auto'
        jingle.volume = Math.max(0, Math.min(1, getSfxLinear()))
        state.shinyJingleAudio = jingle
        void jingle
          .play()
          .then(() => resolve(true))
          .catch(() => {
            if (state.shinyJingleAudio === jingle) state.shinyJingleAudio = null
            resolve(false)
          })
        jingle.addEventListener(
          'ended',
          () => {
            if (state.shinyJingleAudio === jingle) state.shinyJingleAudio = null
          },
          { once: true },
        )
      } catch {
        resolve(false)
      }
    })
  }

  void tryUrl(primary).then((ok) => {
    if (ok) return
    void tryUrl(fallback).then((ok2) => {
      if (!ok2) playShinyJingleFallbackTones()
    })
  })
}

function resolveCatalogUrl(url: string): string | null {
  const list = state.externalCatalog
  const u = url.trim()
  if (!u || !list.length) return null
  for (const t of list) {
    if (t === u) return t
  }
  try {
    const pathU = new URL(u).pathname
    for (const t of list) {
      try {
        if (new URL(t).pathname === pathU) return t
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }
  for (const t of list) {
    if (u.endsWith(t) || t.endsWith(u)) return t
  }
  return null
}

function getNextStreamingTrack(currentUrl: string, step: 1 | -1 = 1): string | null {
  const list = state.externalCatalog
  if (!list.length) return null
  const resolved = resolveCatalogUrl(currentUrl)
  const idx = resolved ? list.indexOf(resolved) : -1
  if (idx < 0) return list[0] ?? null
  const nextIdx = (idx + step + list.length) % list.length
  return list[nextIdx] ?? null
}

/** URL custom hors catalogue => piste épinglée en boucle; piste du catalogue => playlist auto. */
function isPinnedExternalTrack(): boolean {
  const selected = prefs.externalTrackUrl?.trim()
  if (!selected) return false
  return !resolveCatalogUrl(selected)
}

/** Piste custom épinglée ou catalogue mono-piste => boucle; sinon playlist auto. */
function shouldLoopExternalStreaming(): boolean {
  if (isPinnedExternalTrack()) return true
  return state.externalCatalog.length <= 1
}

function crossfadeExternalAudio(nextSrc: string, durationMs = 500) {
  if (typeof window === 'undefined') return
  const transitionToken = ++state.externalTransitionToken
  const targetVolume = Math.max(0, Math.min(1, getMusicLinear()))
  const current = state.externalAudio
  if (state.externalFadeTimer !== null) {
    window.clearInterval(state.externalFadeTimer)
    state.externalFadeTimer = null
  }
  if (state.externalFadingOutAudio && state.externalFadingOutAudio !== current) {
    try {
      state.externalFadingOutAudio.pause()
      state.externalFadingOutAudio.src = ''
    } catch {
      // ignore
    }
    state.externalFadingOutAudio = null
  }
  const next = new Audio(nextSrc)
  next.loop = shouldLoopExternalStreaming()
  next.preload = 'auto'
  next.volume = 0
  try {
    next.dataset.playlistSrc = nextSrc
  } catch {
    // ignore
  }
  state.externalAudio = next
  state.externalFadingOutAudio = current ?? null
  void next.play().then(() => {
    if (transitionToken !== state.externalTransitionToken) {
      try {
        next.pause()
        next.src = ''
      } catch {
        // ignore
      }
      return
    }
    if (prefs.resumeStreamingFromLastPosition) {
      const saved = loadStreamingProgress()
      if (saved && saved.url === nextSrc && saved.timeSec > 0) {
        const seekWhenReady = () => {
          try {
            const maxSeek = Number.isFinite(next.duration) ? Math.max(0, next.duration - 1) : saved.timeSec
            next.currentTime = Math.max(0, Math.min(saved.timeSec, maxSeek))
          } catch {
            // ignore
          }
        }
        if (next.readyState >= 1) {
          seekWhenReady()
        } else {
          next.addEventListener('loadedmetadata', seekWhenReady, { once: true })
        }
      }
    }
    attachStreamingProgressTracking(next, nextSrc)
    state.externalPlayingCanonicalSrc = nextSrc
    if (!next.loop) {
      const onEnded = () => {
        if (state.externalAudio !== next) return
        if (prefs.musicMode !== 'streaming') return
        if (state.externalCatalog.length <= 1) return
        const source = next.dataset.playlistSrc || nextSrc
        const canon = resolveCatalogUrl(source)
        if (!canon) return
        const after = getNextStreamingTrack(canon, 1)
        if (!after || after === canon) return
        crossfadeExternalAudio(after, prefs.streamingCrossfadeMs)
      }
      next.addEventListener('ended', onEnded, { once: true })
    }
    const start = Date.now()
    state.externalFadeTimer = window.setInterval(() => {
      if (transitionToken !== state.externalTransitionToken) {
        if (state.externalFadeTimer !== null) {
          window.clearInterval(state.externalFadeTimer)
          state.externalFadeTimer = null
        }
        try {
          next.pause()
          next.src = ''
        } catch {
          // ignore
        }
        return
      }
      const t = Math.min(1, (Date.now() - start) / durationMs)
      next.volume = targetVolume * t
      if (current) current.volume = targetVolume * (1 - t)
      if (t >= 1) {
        if (state.externalFadeTimer !== null) {
          window.clearInterval(state.externalFadeTimer)
          state.externalFadeTimer = null
        }
        if (current) {
          try {
            current.pause()
            current.src = ''
          } catch {
            // ignore
          }
        }
        if (state.externalFadingOutAudio === current) {
          state.externalFadingOutAudio = null
        }
      }
    }, 40)
  }).catch(() => {
    if (state.externalFadeTimer !== null) {
      window.clearInterval(state.externalFadeTimer)
      state.externalFadeTimer = null
    }
    if (state.externalAudio === next) state.externalAudio = current ?? null
    if (state.externalFadingOutAudio === current) {
      state.externalFadingOutAudio = null
    }
    try {
      next.pause()
      next.src = ''
    } catch {
      // ignore
    }
  })
}

function normalizeTrackPath(raw: string, basePath?: string): string {
  const file = raw.trim()
  if (!file) return ''
  if (/^https?:\/\//i.test(file) || file.startsWith('/')) return file
  if (basePath && basePath.length > 0) {
    return `${basePath.replace(/\/$/, '')}/${file.replace(/^\//, '')}`
  }
  return `/audio/nostalgia/${file.replace(/^\//, '')}`
}

function getAudioPublicBase(): string {
  const config = useRuntimeConfig()
  const apiBaseRaw = String(config.public?.apiBase ?? '').trim()
  if (!apiBaseRaw) return ''
  return apiBaseRaw.replace(/\/$/, '')
}

function toAbsoluteAudioUrl(pathOrUrl: string): string {
  const value = pathOrUrl.trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  const base = getAudioPublicBase()
  // Fichiers sous /audio/ : en prod le proxy front expose une custom location
  // same-origin, ce qui évite de coupler les assets statiques au domaine API.
  if (value.startsWith('/audio/')) {
    return value
  }
  if (!base) return value
  if (value.startsWith('/')) return `${base}${value}`
  return `${base}/${value.replace(/^\//, '')}`
}

function collectTrackLikeStrings(input: unknown, out: string[]) {
  if (typeof input === 'string') {
    if (input.toLowerCase().includes('.ogg')) out.push(input)
    return
  }
  if (Array.isArray(input)) {
    for (const item of input) collectTrackLikeStrings(item, out)
    return
  }
  if (!input || typeof input !== 'object') return
  for (const value of Object.values(input as Record<string, unknown>)) {
    collectTrackLikeStrings(value, out)
  }
}

function classifyTrackContext(path: string): BgmContext {
  const p = path.toLowerCase()
  if (/\b(raid|boss|champion|league|gym|cynthia)\b/.test(p)) return 'boss'
  if (/\b(forest|viridian|eterna)\b/.test(p)) return 'forest'
  if (/\b(cave|mt|mount|victory|lavender)\b/.test(p)) return 'cave'
  if (/\b(ocean|surf|lake|ss_|dive|canalave)\b/.test(p)) return 'ocean'
  if (/\b(volcano|mansion|magma|fire)\b/.test(p)) return 'volcano'
  if (/\b(snow|ice|snowpoint|route_216|snowbelle)\b/.test(p)) return 'snow'
  if (/\b(route|town|city|park|lab|center|area|bicycle|goldenrod|oldale|littleroot|pallet)\b/.test(p)) return 'route'
  return 'menu'
}

async function loadExternalManifestIfNeeded(manifestUrl: string, forceReload = false) {
  if (forceReload) {
    state.externalManifestLoaded = false
    state.externalManifestFailed = false
    state.externalCatalog = []
    state.externalTracksByContext = {
      menu: [],
      route: [],
      forest: [],
      cave: [],
      ocean: [],
      volcano: [],
      snow: [],
      league: [],
      boss: [],
    }
  }
  if (state.externalManifestLoaded || state.externalManifestFailed || typeof window === 'undefined') return
  try {
    const res = await fetch(toAbsoluteAudioUrl(manifestUrl), { cache: 'no-cache' })
    if (!res.ok) throw new Error(`manifest ${res.status}`)
    const manifest = await res.json() as Record<string, unknown>
    const basePath = typeof manifest.basePath === 'string' ? manifest.basePath : '/audio/nostalgia'

    const pools: Record<BgmContext, string[]> = {
      menu: [],
      route: [],
      forest: [],
      cave: [],
      ocean: [],
      volcano: [],
      snow: [],
      league: [],
      boss: [],
    }

    const contexts = manifest.contexts as Record<string, unknown> | undefined
    if (contexts && typeof contexts === 'object') {
      for (const [k, v] of Object.entries(contexts)) {
        const values: string[] = []
        collectTrackLikeStrings(v, values)
        const key = k.toLowerCase()
        const mappedContext: BgmContext =
          key === 'combat' ? 'route' :
          key === 'raid' ? 'boss' :
          key === 'menu' || key === 'route' || key === 'forest' || key === 'cave' ||
          key === 'ocean' || key === 'volcano' || key === 'snow' || key === 'league' ||
          key === 'boss'
            ? key
            : 'menu'
        for (const raw of values) {
          const full = toAbsoluteAudioUrl(normalizeTrackPath(raw, basePath))
          if (full) pools[mappedContext].push(full)
        }
      }
    }

    const fallbackRaw: string[] = []
    collectTrackLikeStrings(manifest, fallbackRaw)
    for (const raw of fallbackRaw) {
      const full = toAbsoluteAudioUrl(normalizeTrackPath(raw, basePath))
      if (!full) continue
      const ctx = classifyTrackContext(full)
      pools[ctx].push(full)
    }

    for (const key of Object.keys(pools) as BgmContext[]) {
      pools[key] = [...new Set(pools[key])]
    }
    state.externalTracksByContext = pools
    state.externalCatalog = [...new Set((Object.values(pools).flat()))]
    state.externalManifestLoaded = true
  } catch {
    state.externalManifestFailed = true
  }
}

function pickExternalTrack(context: BgmContext): string | null {
  const candidates = state.externalTracksByContext[context]
  if (!candidates || candidates.length === 0) return null
  const previous = state.externalLastTrackByContext[context]
  if (candidates.length === 1) return candidates[0] ?? null
  const filtered = candidates.filter((c) => c !== previous)
  const bucket = filtered.length > 0 ? filtered : candidates
  const next = bucket[Math.floor(Math.random() * bucket.length)] ?? null
  if (next) state.externalLastTrackByContext[context] = next
  return next
}

function semitoneToRatio(semitones: number): number {
  return Math.pow(2, semitones / 12)
}

function getOstGenerationProfile(gen: number): {
  transposeSemitones: number
  bassDivisor: number
  leadAccentMul: number
} {
  // Couleurs régionales: léger décalage tonal + assise rythmique par génération.
  if (gen <= 2) return { transposeSemitones: 0, bassDivisor: 2.02, leadAccentMul: 1.02 }
  if (gen <= 4) return { transposeSemitones: 2, bassDivisor: 1.95, leadAccentMul: 1.04 }
  if (gen <= 5) return { transposeSemitones: -2, bassDivisor: 2.08, leadAccentMul: 1.05 }
  if (gen <= 7) return { transposeSemitones: 3, bassDivisor: 1.9, leadAccentMul: 1.08 }
  return { transposeSemitones: -1, bassDivisor: 1.86, leadAccentMul: 1.1 }
}

function getOstContextHarmony(context: BgmContext): {
  harmonySemitones: number[]
  counterSemitones: number[]
} {
  if (context === 'boss') {
    return {
      harmonySemitones: [0, 7, 10, 12],
      counterSemitones: [12, 7, 10, 14],
    }
  }
  if (context === 'menu') {
    return {
      harmonySemitones: [0, 4, 7, 12],
      counterSemitones: [12, 9, 7, 4],
    }
  }
  return {
    harmonySemitones: [0, 4, 7, 11],
    counterSemitones: [12, 11, 9, 7],
  }
}

function getAudioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (state.audioCtx) return state.audioCtx
  const Ctx = window.AudioContext || (window as any).webkitAudioContext
  if (!Ctx) return null
  state.audioCtx = new Ctx()
  return state.audioCtx
}

function toLinearVolume(percent: number): number {
  const clamped = Math.max(0, Math.min(100, percent))
  return (clamped / 100) ** 1.6
}

function getMasterLinear(): number {
  if (!prefs.enabled) return 0
  return toLinearVolume(prefs.masterVolume)
}

function getMusicLinear(): number {
  if (!prefs.enabled || !prefs.musicEnabled) return 0
  return getMasterLinear() * toLinearVolume(prefs.musicVolume)
}

function getSfxLinear(): number {
  if (!prefs.enabled || !prefs.sfxEnabled) return 0
  return getMasterLinear() * toLinearVolume(prefs.sfxVolume)
}

function clearBgmNodes() {
  for (const osc of state.bgmOscillators) {
    try { osc.stop() } catch { /* ignore */ }
    try { osc.disconnect() } catch { /* ignore */ }
  }
  for (const gain of state.bgmGains) {
    try { gain.disconnect() } catch { /* ignore */ }
  }
  state.bgmOscillators = []
  state.bgmGains = []
}

function stopBgm() {
  if (state.bgmStepTimer) {
    clearTimeout(state.bgmStepTimer)
    state.bgmStepTimer = null
  }
  if (state.bgmContextTimer) {
    clearTimeout(state.bgmContextTimer)
    state.bgmContextTimer = null
  }
  clearBgmNodes()
  stopExternalAudio()
}

function fadeOutBgm(ms: number) {
  const ctx = getAudioCtx()
  if (!ctx || state.bgmGains.length === 0) return
  const now = ctx.currentTime
  const end = now + ms / 1000
  for (const gain of state.bgmGains) {
    try {
      const current = Math.max(0.0001, gain.gain.value)
      gain.gain.cancelScheduledValues(now)
      gain.gain.setValueAtTime(current, now)
      gain.gain.exponentialRampToValueAtTime(0.0001, end)
    } catch {
      // ignore
    }
  }
}

function ensureBgmVoice(ctx: AudioContext) {
  if (state.bgmOscillators.length > 0) return
  const oscA = ctx.createOscillator()
  const oscB = ctx.createOscillator()
  const oscC = ctx.createOscillator()
  const gainA = ctx.createGain()
  const gainB = ctx.createGain()
  const gainC = ctx.createGain()
  const pack = AUDIO_PACKS[getActivePack()]
  oscA.type = pack.leadWave
  oscB.type = pack.bassWave
  oscC.type = 'triangle'
  gainA.gain.value = 0
  gainB.gain.value = 0
  gainC.gain.value = 0
  oscA.connect(gainA).connect(ctx.destination)
  oscB.connect(gainB).connect(ctx.destination)
  oscC.connect(gainC).connect(ctx.destination)
  oscA.start()
  oscB.start()
  oscC.start()
  state.bgmOscillators = [oscA, oscB, oscC]
  state.bgmGains = [gainA, gainB, gainC]
}

function stepBgm() {
  const ctx = getAudioCtx()
  if (!ctx || !state.unlocked) return
  const pattern = getActivePattern(state.currentBgm)
  const baseNote = pattern.notes[state.bgmStep % pattern.notes.length] ?? 220
  state.bgmStep++
  const pack = AUDIO_PACKS[getActivePack()]
  const dur = 60 / (pattern.bpm * pack.bpmScale)
  const vol = getMusicLinear()
  const now = ctx.currentTime
  ensureBgmVoice(ctx)
  const [oscA, oscB, oscC] = state.bgmOscillators
  const [gainA, gainB, gainC] = state.bgmGains
  if (!oscA || !oscB || !gainA || !gainB) return

  let leadFreq = baseNote
  let bassFreq = baseNote / 2
  let harmonyFreq = baseNote / 4
  let leadAccent = 1
  let harmonyAccent = 0.45

  if (getActivePack() === 'ostInspired') {
    const profile = getOstGenerationProfile(state.generationContext)
    const harmony = getOstContextHarmony(state.currentBgm)
    const transposeRatio = semitoneToRatio(profile.transposeSemitones)
    const stepPulse = state.bgmStep % 2 === 0 ? 1.06 : 0.97
    const chordIdx = state.bgmStep % harmony.harmonySemitones.length
    const counterIdx = state.bgmStep % harmony.counterSemitones.length
    const harmonicBoost = semitoneToRatio(harmony.harmonySemitones[chordIdx] ?? 0)
    const counterBoost = semitoneToRatio(harmony.counterSemitones[counterIdx] ?? 12)
    leadFreq = baseNote * transposeRatio * harmonicBoost * stepPulse
    bassFreq = (baseNote * transposeRatio) / profile.bassDivisor
    harmonyFreq = baseNote * transposeRatio * counterBoost
    leadAccent = profile.leadAccentMul * (state.currentBgm === 'boss' ? 1.08 : 1)
    harmonyAccent = state.currentBgm === 'boss' ? 0.62 : 0.48
  }

  oscA.frequency.setValueAtTime(leadFreq, now)
  oscB.frequency.setValueAtTime(bassFreq, now)
  if (oscC) oscC.frequency.setValueAtTime(harmonyFreq, now)
  gainA.gain.cancelScheduledValues(now)
  gainB.gain.cancelScheduledValues(now)
  gainC?.gain.cancelScheduledValues(now)
  gainA.gain.setValueAtTime(0, now)
  gainB.gain.setValueAtTime(0, now)
  gainC?.gain.setValueAtTime(0, now)
  gainA.gain.linearRampToValueAtTime(vol * 0.25 * pack.leadGainMul * leadAccent, now + 0.03)
  gainA.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol * 0.02 * pack.leadGainMul * leadAccent), now + dur * 0.95)
  gainB.gain.linearRampToValueAtTime(vol * 0.1 * pack.bassGainMul, now + 0.02)
  gainB.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol * 0.01 * pack.bassGainMul), now + dur * 0.9)
  if (gainC) {
    gainC.gain.linearRampToValueAtTime(vol * 0.12 * harmonyAccent, now + 0.04)
    gainC.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol * 0.015 * harmonyAccent), now + dur * 0.88)
  }
  state.bgmStepTimer = setTimeout(stepBgm, Math.max(60, Math.round(dur * 1000)))
}

function playTone(freq: number, durationMs: number, gainValue: number, wave: OscillatorType = 'triangle') {
  const ctx = getAudioCtx()
  if (!ctx || !state.unlocked) return
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  const now = ctx.currentTime
  osc.type = wave
  osc.frequency.setValueAtTime(freq, now)
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, gainValue), now + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000)
  osc.connect(gain).connect(ctx.destination)
  osc.start(now)
  osc.stop(now + durationMs / 1000 + 0.02)
}

function canPlayNow(key: string, cooldownMs: number): boolean {
  const now = Date.now()
  const last = state.lastSfxAt[key] ?? 0
  if (now - last < cooldownMs) return false
  state.lastSfxAt[key] = now
  return true
}

async function fetchCryUrl(slug: string): Promise<string | null> {
  if (!slug) return null
  if (state.cryCache.has(slug)) {
    const cached = state.cryCache.get(slug) ?? null
    setCryCacheEntry(slug, cached)
    return cached
  }
  try {
    const res = await fetch(`https://pokeapi.co/api/v2/pokemon/${encodeURIComponent(slug)}`)
    if (!res.ok) throw new Error(`PokeAPI ${res.status}`)
    const data = await res.json() as { cries?: { latest?: string; legacy?: string } }
    const url = data.cries?.latest || data.cries?.legacy || null
    setCryCacheEntry(slug, url)
    return url
  } catch {
    setCryCacheEntry(slug, null)
    return null
  }
}

function isSfxCategoryAllowed(name: SfxName): boolean {
  switch (name) {
    case 'uiToggle':
      return prefs.sfxUiEnabled
    case 'pokeballThrow':
    case 'pokeballLock':
    case 'summon':
      return prefs.sfxInvocationEnabled
    case 'encounter':
      return prefs.sfxEncounterEnabled
    case 'shinyEncounter':
      return prefs.sfxShinyEncounterEnabled
    case 'bossStart':
    case 'bossFailed':
      return prefs.sfxBossEventsEnabled
    case 'enemyDefeated':
    case 'bossDefeated':
      return prefs.sfxCombatEnabled
    case 'levelUp':
    case 'badge':
      return prefs.sfxProgressEnabled
    default:
      return true
  }
}

export function useAudioManager() {
  function unlockAudio() {
    const ctx = getAudioCtx()
    if (!ctx) return
    if (ctx.state === 'suspended') {
      void ctx.resume()
    }
    state.unlocked = true
    refreshBgm()
  }

  function setPreferences(next: Partial<AudioPreferences>) {
    const prevManifest = prefs.externalTrackUrl
    prefs.enabled = next.enabled ?? prefs.enabled
    prefs.musicEnabled = next.musicEnabled ?? prefs.musicEnabled
    prefs.sfxEnabled = next.sfxEnabled ?? prefs.sfxEnabled
    prefs.sfxUiEnabled = next.sfxUiEnabled ?? prefs.sfxUiEnabled
    prefs.sfxInvocationEnabled = next.sfxInvocationEnabled ?? prefs.sfxInvocationEnabled
    prefs.sfxEncounterEnabled = next.sfxEncounterEnabled ?? prefs.sfxEncounterEnabled
    prefs.sfxShinyEncounterEnabled = next.sfxShinyEncounterEnabled ?? prefs.sfxShinyEncounterEnabled
    prefs.sfxBossEventsEnabled = next.sfxBossEventsEnabled ?? prefs.sfxBossEventsEnabled
    prefs.sfxCombatEnabled = next.sfxCombatEnabled ?? prefs.sfxCombatEnabled
    prefs.sfxProgressEnabled = next.sfxProgressEnabled ?? prefs.sfxProgressEnabled
    prefs.criesEnabled = next.criesEnabled ?? prefs.criesEnabled
    prefs.wildKillSfxEnabled = next.wildKillSfxEnabled ?? prefs.wildKillSfxEnabled
    prefs.musicMode = next.musicMode ?? prefs.musicMode
    prefs.externalTrackUrl = next.externalTrackUrl ?? prefs.externalTrackUrl
    prefs.resumeStreamingFromLastPosition = next.resumeStreamingFromLastPosition ?? prefs.resumeStreamingFromLastPosition
    prefs.shinyEasterEggEnabled = next.shinyEasterEggEnabled ?? prefs.shinyEasterEggEnabled
    prefs.audioPack = next.audioPack ?? prefs.audioPack
    prefs.autoPackByGeneration = next.autoPackByGeneration ?? prefs.autoPackByGeneration
    prefs.streamingCrossfadeMs = Math.max(200, Math.min(2000, Math.round(next.streamingCrossfadeMs ?? prefs.streamingCrossfadeMs)))
    prefs.masterVolume = Math.max(0, Math.min(100, Math.round(next.masterVolume ?? prefs.masterVolume)))
    prefs.musicVolume = Math.max(0, Math.min(100, Math.round(next.musicVolume ?? prefs.musicVolume)))
    prefs.sfxVolume = Math.max(0, Math.min(100, Math.round(next.sfxVolume ?? prefs.sfxVolume)))
    if (prefs.externalTrackUrl !== prevManifest) {
      state.externalManifestLoaded = false
      state.externalManifestFailed = false
      state.externalTracksByContext = {
        menu: [],
        route: [],
        forest: [],
        cave: [],
        ocean: [],
        volcano: [],
        snow: [],
        league: [],
        boss: [],
      }
    }
    refreshBgm()
  }

  function syncFromPlayer(playerPrefs: AudioPreferences | null | undefined) {
    if (!playerPrefs) return
    setPreferences(playerPrefs)
  }

  function refreshBgm() {
    const shouldPlay = state.unlocked && prefs.enabled && prefs.musicEnabled
    if (!shouldPlay) {
      stopBgm()
      return
    }
    const streamingPreferred = prefs.musicMode === 'streaming'
    const generatedAllowed = prefs.musicMode === 'generated'
    if (streamingPreferred) {
      // Ensure generated engine is fully stopped in jukebox mode.
      if (state.bgmStepTimer) {
        clearTimeout(state.bgmStepTimer)
        state.bgmStepTimer = null
      }
      if (state.bgmContextTimer) {
        clearTimeout(state.bgmContextTimer)
        state.bgmContextTimer = null
      }
      clearBgmNodes()
      const directTrack = prefs.externalTrackUrl?.trim()
      if (directTrack) {
        if (state.externalAudio?.src === directTrack || state.externalAudio?.src.endsWith(directTrack)) {
          state.externalAudio.volume = Math.max(0, Math.min(1, getMusicLinear()))
          return
        }
        crossfadeExternalAudio(directTrack, prefs.streamingCrossfadeMs)
        return
      }
      // Jukebox mode: keep currently playing streaming track across page/context changes.
      if (state.externalAudio?.src) {
        state.externalAudio.volume = Math.max(0, Math.min(1, getMusicLinear()))
        return
      }
      const manifestUrl = '/audio/nostalgia/manifest.json'
      void loadExternalManifestIfNeeded(manifestUrl).then(() => {
        const catalog = state.externalCatalog
        const src = catalog.length > 0
          ? catalog[Math.floor(Math.random() * catalog.length)] ?? null
          : null
        if (!src) {
          stopExternalAudio()
          if (generatedAllowed && !state.bgmStepTimer) {
            state.bgmStep = 0
            stepBgm()
          }
          return
        }
        if (state.externalAudio?.src === src || state.externalAudio?.src.endsWith(src)) {
          state.externalAudio.volume = Math.max(0, Math.min(1, getMusicLinear()))
          return
        }
        crossfadeExternalAudio(src, prefs.streamingCrossfadeMs)
      })
      if (!generatedAllowed) return
    }
    stopExternalAudio()
    if (!state.bgmStepTimer) {
      state.bgmStep = 0
      stepBgm()
    }
  }

  function previewPack(pack: AudioPack) {
    const beforePack = prefs.audioPack
    const beforeEnabled = prefs.enabled
    const beforeMusic = prefs.musicEnabled
    const beforeAutoByGen = prefs.autoPackByGeneration
    setPreferences({
      enabled: true,
      musicEnabled: true,
      audioPack: pack,
      autoPackByGeneration: false,
    })
    unlockAudio()
    playSfx('uiToggle')
    const previewOrder: BgmContext[] = ['menu', 'route', 'boss']
    let idx = 0
    const run = () => {
      setBgmContext(previewOrder[idx] ?? 'menu')
      idx++
      if (idx >= previewOrder.length) {
        setTimeout(() => {
          setPreferences({
            enabled: beforeEnabled,
            musicEnabled: beforeMusic,
            audioPack: beforePack,
            autoPackByGeneration: beforeAutoByGen,
          })
          refreshBgm()
        }, 1200)
        return
      }
      setTimeout(run, 1100)
    }
    run()
  }

  function setBgmContext(context: BgmContext) {
    if (state.currentBgm === context) return
    if (prefs.musicMode === 'streaming') {
      // In jukebox streaming mode we keep one global background track.
      state.currentBgm = context
      refreshBgm()
      return
    }
    const shouldCrossfade = Boolean(state.bgmStepTimer)
    if (!shouldCrossfade) {
      state.currentBgm = context
      state.bgmStep = 0
      refreshBgm()
      return
    }
    fadeOutBgm(220)
    if (state.bgmContextTimer) clearTimeout(state.bgmContextTimer)
    state.bgmContextTimer = setTimeout(() => {
      stopBgm()
      state.currentBgm = context
      state.bgmStep = 0
      refreshBgm()
    }, 220)
  }

  function setGenerationContext(generation: number) {
    const nextGen = Math.max(1, Math.floor(generation || 1))
    if (state.generationContext === nextGen) return
    state.generationContext = nextGen
    if (prefs.autoPackByGeneration && state.bgmStepTimer) {
      state.bgmStep = 0
      refreshBgm()
    }
  }

  function getActivePackId(): AudioPack {
    return getActivePack()
  }

  async function loadStreamingCatalog(forceReload = false) {
    const manifestUrl = '/audio/nostalgia/manifest.json'
    await loadExternalManifestIfNeeded(manifestUrl, forceReload)
    return [...state.externalCatalog]
  }

  function getStreamingCatalog(): string[] {
    return [...state.externalCatalog]
  }

  function getRandomStreamingTrack(exceptUrl?: string): string | null {
    const list = state.externalCatalog
    if (!list.length) return null
    if (list.length === 1) return list[0] ?? null
    const pool = exceptUrl ? list.filter((t) => t !== exceptUrl) : list
    return pool[Math.floor(Math.random() * pool.length)] ?? null
  }

  function playSfx(name: SfxName) {
    if (!prefs.enabled || !prefs.sfxEnabled) return
    if (!isSfxCategoryAllowed(name)) return
    const vol = getSfxLinear()
    if (vol <= 0 || !state.unlocked) return
    const key = `sfx:${name}`
    if (!canPlayNow(key, name === 'enemyDefeated' ? 120 : 50)) return
    switch (name) {
      case 'uiToggle':
        playTone(760, 80, vol * 0.2, 'square')
        break
      case 'pokeballThrow':
        if (playEffectSample('/audio/effect/pokeball.ogg', 1)) break
        // "Woosh + click" court façon lancer de Pokeball.
        playTone(520, 60, vol * 0.14, 'triangle')
        setTimeout(() => playTone(380, 90, vol * 0.12, 'triangle'), 35)
        setTimeout(() => playTone(900, 40, vol * 0.17, 'square'), 110)
        break
      case 'pokeballLock':
        // Petit "clac" net avant ouverture/révélation.
        playTone(980, 30, vol * 0.18, 'square')
        setTimeout(() => playTone(640, 45, vol * 0.13, 'triangle'), 18)
        break
      case 'encounter':
        playTone(440, 120, vol * 0.15)
        break
      case 'shinyEncounter':
        playShinyJingle()
        break
      case 'bossStart':
        playTone(220, 180, vol * 0.24, 'sawtooth')
        setTimeout(() => playTone(261.63, 180, vol * 0.2, 'sawtooth'), 120)
        break
      case 'enemyDefeated':
        playTone(520, 90, vol * 0.15)
        break
      case 'bossDefeated':
        playTone(523.25, 120, vol * 0.25, 'triangle')
        setTimeout(() => playTone(659.25, 130, vol * 0.23, 'triangle'), 80)
        setTimeout(() => playTone(783.99, 220, vol * 0.2, 'triangle'), 160)
        break
      case 'bossFailed':
        playTone(220, 150, vol * 0.2, 'sawtooth')
        setTimeout(() => playTone(196, 180, vol * 0.18, 'sawtooth'), 90)
        break
      case 'levelUp':
        playTone(659.25, 120, vol * 0.2)
        setTimeout(() => playTone(783.99, 180, vol * 0.22), 90)
        break
      case 'badge':
        playTone(523.25, 120, vol * 0.22)
        setTimeout(() => playTone(659.25, 120, vol * 0.22), 80)
        setTimeout(() => playTone(1046.5, 220, vol * 0.2), 160)
        break
      case 'summon':
        playTone(329.63, 120, vol * 0.16, 'square')
        setTimeout(() => playTone(493.88, 160, vol * 0.18, 'triangle'), 70)
        break
      default:
        playTone(440, 100, vol * 0.15)
    }
  }

  async function playPokemonCry(slug: string) {
    if (!prefs.enabled || !prefs.criesEnabled || !state.unlocked) return
    if (!canPlayNow(`cry:${slug}`, 1200)) return
    const cryUrl = await fetchCryUrl(slug)
    if (!cryUrl) return
    playOneShotUrl(cryUrl, getSfxLinear() * 1.2)
  }

  function shutdown() {
    stopBgm()
    stopShinyJingle()
    for (const el of oneShotPool) releaseOneShotElement(el)
  }

  return {
    state: readonly(state),
    prefs: readonly(prefs),
    unlockAudio,
    setPreferences,
    syncFromPlayer,
    setGenerationContext,
    getActivePackId,
    loadStreamingCatalog,
    getStreamingCatalog,
    getNextStreamingTrack,
    getRandomStreamingTrack,
    setBgmContext,
    playSfx,
    playPokemonCry,
    previewPack,
    audioPacks: AUDIO_PACKS,
    refreshBgm,
    shutdown,
  }
}
